import { classSet } from '@platform/ui-library';
import { FC } from 'react';
import { FormControlWithOptionProps, Option } from '@app/types';
import { ControlWithFormField, FormFieldControlProps } from './form-field';
import styles from './RadioGroup.module.scss';

export type RadioGroupControlProps<T> = (
  FormControlWithOptionProps<T, Option<T>>
  & {
    options: Option<T>[];
    /* How to display the group @default 'column' for 3+ @see options. */
    theme?: 'row' | 'column';
  }
);

/** Radio button group form control used to select a value out an array of options. */
export function RadioGroupControl<T>({
  onChange,
  options,
  theme,
  value,
  ...inputProps
}: RadioGroupControlProps<T>): ReturnType<FC> {
  theme ??= (options.length > 2) ? 'column' : 'row';
  const groupClassName = classSet([
    styles.radioOptions,
    (theme === 'row') ? 'flex-gap flex-row flex-wrap' : 'flex-column']
  );

  return (
    <div className={groupClassName}>
      {options.map((option) =>
        <label className={styles.radioOption} key={option.label}>
          <input
            {...inputProps}
            type="radio"
            checked={option.value === value}
            onChange={() => onChange(option.value, option)}
          />
          {option.label}
        </label>
      )}
    </div>
  );
}

export type RadioGroupProps<T> = RadioGroupControlProps<T> & FormFieldControlProps;

export function RadioGroup<T>(props: RadioGroupProps<T>): ReturnType<FC> {
  return (
    <ControlWithFormField control={RadioGroupControl<T>} {...props} />
  );
}
